import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
} from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import {
  AbgButton,
  AbgForm,
  AbgTypography,
  mergeSx,
  TextFormControl,
  useTranslation,
} from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useGlobalRegions } from '../../../../../hooks';
import { useAnalyticsLoginFunnel } from '../../../../../shared/analytics/hooks';
import { GlobalRegion } from '../../../../../shared/constants';
import { LoginFormData } from '../../../../../shared/types/LoginFrom.interface';
import { userStoreSelector } from '../../../../../store';
import { GlobalLink } from '../../../../GlobalLink';
import { appendFormTypeParameter } from '../../../utils';

import { innerFormStyling } from './InnerFormPasswordLess.styling';
import { InnerFormProps } from './types';

export const InnerFormPasswordLess = ({
  styleParts = {},
  model,
  onSubmit,
}: InnerFormProps) => {
  const { features } = usePageContext();
  const { belongsToRegions } = useGlobalRegions();
  const t = useTranslation(model.i18n);
  const recognizedProfile = userStoreSelector.use.recognizedProfile();
  const isEmea = belongsToRegions([GlobalRegion.EMEA]);

  const isSimplifiedFlow =
    features['feature.forgotUsernamePasswordSimplifiedFlow'];
  const isOtpFlow = features['feature.loginOTPFlow'];

  const recordAnalyticsLoginFunnel = useAnalyticsLoginFunnel();

  const formMethods = useForm<LoginFormData>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      username: recognizedProfile?.profileNumber ?? '',
      password: '',
    },
  });

  const { handleSubmit, register, trigger } = formMethods;

  const submitHandler: SubmitHandler<LoginFormData> = (data) => {
    recordAnalyticsLoginFunnel({
      method: 'Wizard ID',
      login_state: 'login',
      step_number: 1,
      step_description: 'Overview',
    });
    return onSubmit(data);
  };

  // Handle form submission with validation
  const onFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Trigger validation for all fields before submitting
    const isValid = await trigger();
    if (isValid) {
      await handleSubmit(submitHandler)();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <AbgForm onSubmit={onFormSubmit}>
        {model.showSimplifiedDriveForm ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <AbgTypography variant="body2" sx={{ mb: 0.5 }}>
                {model.userIdFieldTitle}
              </AbgTypography>
              <TextFormControl
                name="username"
                type="text"
                placeholder={model.userIdFieldPlaceholder}
                validationRules={{
                  required: t('global.field.validation.fieldRequired.message', {
                    field: model.userIdFieldTitle,
                    interpolation: { escapeValue: false },
                  }),
                }}
                textFieldProps={{
                  fullWidth: true,
                  sx: innerFormStyling.inputHeight,
                }}
                testIdPrefix="login-form"
                testId="username"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <AbgTypography variant="body2" sx={{ mb: 0.5 }}>
                {model.passwordFieldTitle}
              </AbgTypography>
              <TextFormControl
                name="password"
                type="password"
                placeholder={model.passwordFieldPlaceholder}
                validationRules={{
                  required: t('global.field.validation.fieldRequired.message', {
                    field: model.passwordFieldTitle,
                    interpolation: { escapeValue: false },
                  }),
                }}
                textFieldProps={{
                  fullWidth: true,
                  sx: innerFormStyling.inputHeight,
                }}
                testIdPrefix="login-form"
                testId="password"
              />
            </Box>
            <AbgButton
              variant="contained"
              type="submit"
              data-testid="login-form-submit-btn"
              sx={innerFormStyling.simplifiedSubmitBtn}
            >
              {model.loginCtaText || 'Log in'}
            </AbgButton>
            <GlobalLink
              href={model.forgotUsernameOrPasswordLink}
              target={model.forgotUsernameOrPasswordTarget}
              dataTestId="login-form-forgot-link-simplified"
              sx={innerFormStyling.simplifiedForgotLink}
            >
              {model.forgotUsernameOrPasswordText || 'Forgot your details?'}
            </GlobalLink>
          </Stack>
        ) : (
          <Stack direction="column" sx={innerFormStyling.container}>
            <TextFormControl
              name="username"
              type="text"
              label={model.userIdFieldTitle}
              placeholder={model.userIdFieldPlaceholder}
              validationRules={{
                required: t('global.field.validation.fieldRequired.message', {
                  field: model.userIdFieldTitle,
                  interpolation: { escapeValue: false },
                }),
              }}
              textFieldProps={{
                fullWidth: true,
              }}
              testIdPrefix="login-form"
              testId="username"
            />
            <Box>
              <Box sx={innerFormStyling.ctaContainer}>
                {model.rememberMeEnabled && (
                  <FormGroup
                    data-testid="login-form-rememberme"
                    sx={mergeSx(
                      innerFormStyling.rememberMe,
                      styleParts.rememberMe,
                    )}
                  >
                    <FormControlLabel
                      control={<Checkbox {...register('remember_me')} />}
                      data-testid="rememberMe"
                      label={t('loginModal.rememberMe.label')}
                    />
                  </FormGroup>
                )}

                {!isSimplifiedFlow && (
                  <Box
                    sx={mergeSx(innerFormStyling.ctaContainer, {
                      width: model.additionalForgotPasswordEnabled
                        ? '100%'
                        : 'auto',
                    })}
                  >
                    <GlobalLink
                      href={model.forgotUsernameOrPasswordLink}
                      target={model.forgotUsernameOrPasswordTarget}
                      dataTestId="login-form-forgot-link"
                      sx={innerFormStyling.forgotLink}
                      variant="text"
                      className="secondaryText"
                    >
                      <AbgTypography
                        component="span"
                        variant="actionSmallRegular"
                      >
                        {model.forgotUsernameOrPasswordText}
                      </AbgTypography>
                    </GlobalLink>

                    {model.additionalForgotPasswordEnabled && (
                      <GlobalLink
                        href={model.additionalForgotPasswordLink}
                        target={model.additionalForgotPasswordTarget}
                        dataTestId="login-form-additional-forgot-link"
                        sx={innerFormStyling.forgotLink}
                        variant="text"
                        className="secondaryText"
                      >
                        <AbgTypography
                          component="span"
                          variant="actionSmallRegular"
                        >
                          {model.additionalForgotPasswordText}
                        </AbgTypography>
                      </GlobalLink>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>
        )}

        {!model.showSimplifiedDriveForm && (
          <AbgButton
            variant="contained"
            type="submit"
            data-testid="login-form-submit-btn"
            sx={mergeSx(innerFormStyling.logInBtn, styleParts.logInBtn)}
          >
            {model.loginCtaText}
          </AbgButton>
        )}

        {isSimplifiedFlow && (
          <Stack
            direction="row"
            columnGap={2}
            justifyContent="center"
            sx={innerFormStyling.forgotLinksWrapper}
          >
            {!isEmea && (
              <GlobalLink
                href={appendFormTypeParameter(
                  model.forgotUsernameOrPasswordLink,
                  'look_up_username',
                )}
                target={model.forgotUsernameOrPasswordTarget}
                dataTestId="login-form-forgot-link"
                variant="text"
                className="secondaryText"
              >
                <AbgTypography component="span" variant="bodySmallRegular">
                  {model.separateForgotUsernameText}
                </AbgTypography>
              </GlobalLink>
            )}

            {!isEmea && !isOtpFlow && (
              <Divider
                orientation="vertical"
                flexItem
                sx={innerFormStyling.divider}
              />
            )}

            {!isOtpFlow && (
              <GlobalLink
                href={appendFormTypeParameter(
                  model.forgotUsernameOrPasswordLink,
                  'reset_password',
                )}
                target={model.forgotUsernameOrPasswordTarget}
                dataTestId="login-form-forgot-link"
                variant="text"
                className="secondaryText"
              >
                <AbgTypography component="span" variant="bodySmallRegular">
                  {model.separateForgotPasswordText}
                </AbgTypography>
              </GlobalLink>
            )}
          </Stack>
        )}
      </AbgForm>
    </FormProvider>
  );
};
